class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = {};
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.isOpen = false;

    this.setupEventListeners();
  }

  setupEventListeners() {
    const form = this.querySelector('form.search');
    form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('input', debounce((event) => {
      this.onChange(event);
    }, 300).bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));

    if (this.predictiveSearchResults) {
      this.predictiveSearchResults.addEventListener('click', this.onResultsBackdropClick.bind(this));
      this.predictiveSearchResults.addEventListener('click', this.onResultsClick.bind(this));
    }
    
    // Recalculate fixed positioning on viewport changes (header flyover + drawer sheet)
    const reflowOnViewport = this.closest('.header__search-wrapper') || this.closest('.menu-drawer');
    if (reflowOnViewport) {
      window.addEventListener('resize', () => {
        if (this.isOpen) {
          this.open();
        }
      });
      window.addEventListener('scroll', () => {
        if (this.isOpen) {
          this.open();
        }
      });
    }
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFocus() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) return;

    if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.contains(document.activeElement)) return;

      const isHeaderSearch = !!this.closest('.header__search-wrapper');
      const isDrawerSearch = !!this.closest('.menu-drawer');
      const isTemplateSearch = !!this.closest('.template-search__search');
      const isMobile = window.matchMedia('(max-width: 989px)').matches;
      const hasQuery = this.getQuery().length > 0;

      // Mobile keyboard dismiss fires blur/focusout without meaning “close results”.
      // Keep the panel open while there’s still a query (header, drawer, search template).
      if (
        (isHeaderSearch || isDrawerSearch || isTemplateSearch) &&
        isMobile &&
        hasQuery
      )
        return;

      this.close();
    })
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up')
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (
      event.code === 'ArrowUp' ||
      event.code === 'ArrowDown'
    ) {
      event.preventDefault();
    }
  }

  onResultsBackdropClick(event) {
    const resultsPanel = this.predictiveSearchResults.querySelector('#predictive-search-results');
    if (!resultsPanel) return;
    if (!resultsPanel.contains(event.target)) {
      this.close();
      if (this.input) this.input.blur();
    }
  }

  onResultsClick(event) {
    const viewAllButton = event.target.closest('.predictive-search__view-all-button');
    if (!viewAllButton) return;

    event.preventDefault();

    const query = this.getQuery();
    if (!query.length) return;

    const form = this.querySelector('form.search');
    const action = form?.getAttribute('action') || routes.search_url;
    const params = new URLSearchParams(new FormData(form));
    params.set('q', query);

    window.location.assign(`${action}?${params.toString()}`);
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allElements = this.querySelectorAll('li');
    let activeElement = this.querySelector('li');

    if (moveUp && !selectedElement) return;

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElement = selectedElement.nextElementSibling || allElements[0];
    } else if (moveUp) {
      activeElement = selectedElement.previousElementSibling || allElements[allElements.length - 1];
    }

    if (activeElement === selectedElement) return;

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.setLiveRegionText(activeElement.textContent);
    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedProduct = this.querySelector('[aria-selected="true"] a, [aria-selected="true"] button');

    if (selectedProduct) selectedProduct.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(" ", "-").toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&${encodeURIComponent('resources[type]')}=product&${encodeURIComponent('resources[limit]')}=10&section_id=predictive-search&type=product`)
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search').innerHTML;
        this.cachedResults[queryKey] = resultsMarkup;
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  viewportHeight() {
    return window.visualViewport?.height ?? window.innerHeight;
  }

  mobileChromeReserve() {
    return window.matchMedia('(max-width: 989px)').matches ? 104 : 36;
  }

  /** Distance from viewport bottom up to the fixed pill nav top (tighter than a flat px guess). */
  bottomInsetPx() {
    const vh = this.viewportHeight();
    const nav = document.querySelector('mobile-bottom-navigation.mobile-bottom-nav');
    if (!nav || getComputedStyle(nav).display === 'none') {
      return this.mobileChromeReserve();
    }
    const inset = Math.round(vh - nav.getBoundingClientRect().top);
    return Math.min(Math.max(52, inset), Math.round(vh * 0.42));
  }

  /**
   * Drawer: fixed sheet flush under the visible search row.
   * Do not use `form.search` — the results panel (`[data-predictive-search]`) is a sibling after `.field`;
   * before it is `position:fixed` it stays in-flow and inflates `form.bottom` (~191px gap bug).
   * Do not use `.search-modal` either (oversized box). Use `.field` + input only.
   */
  drawerPredictiveTopPx() {
    if (!this.input) return 0;
    const field = this.input.closest('.field');
    const bottoms = [
      field?.getBoundingClientRect().bottom,
      this.input.getBoundingClientRect().bottom,
    ].filter((b) => typeof b === 'number' && !Number.isNaN(b) && b > 0);

    if (!bottoms.length) return 0;

    return Math.round(Math.max(...bottoms));
  }

  getResultsMaxHeight() {
    const viewportH = this.viewportHeight();
    let topBound = 88;

    if (this.input) {
      const r = this.input.getBoundingClientRect();
      if (r.bottom > -60 && r.bottom < viewportH + 320) {
        topBound = Math.max(topBound, r.bottom + 10);
      }
    }

    const bottomReserve = this.mobileChromeReserve();
    let maxH = viewportH - topBound - bottomReserve;
    maxH = Math.min(maxH, viewportH * 0.88);
    maxH = Math.max(180, maxH);

    this.resultsMaxHeight = maxH;
    return maxH;
  }

  open() {
    // Check if this is the header predictive search
    const isHeaderSearch = this.closest('.header__search-wrapper');
    let refineDrawerPredictiveTop = false;

    const bottomPx = window.matchMedia('(max-width: 989px)').matches
      ? this.bottomInsetPx()
      : this.mobileChromeReserve();

    if (isHeaderSearch) {
      // For header search, use same top offset as search flyover.
      const inputWrapper = this.input.closest('.header__search-input-wrapper');
      const searchSection = this.input.closest('.header__search-section');
      const anchorEl = inputWrapper || searchSection;
      const anchorRect = anchorEl?.getBoundingClientRect();
      const searchFlyover = document.getElementById('search-flyover');
      const computedTop = anchorRect ? (anchorRect.bottom + 12) + 'px' : null;
      const topValue = searchFlyover?.style?.top || computedTop || '95px';

      // Fill viewport down to pill nav so background isn’t a short strip over the drawer
      this.predictiveSearchResults.style.position = 'fixed';
      this.predictiveSearchResults.style.left = '0';
      this.predictiveSearchResults.style.right = '0';
      this.predictiveSearchResults.style.width = '100%';
      this.predictiveSearchResults.style.maxWidth = '100%';
      this.predictiveSearchResults.style.top = topValue;
      this.predictiveSearchResults.style.bottom = `${bottomPx}px`;
      this.predictiveSearchResults.style.height = 'auto';
      this.predictiveSearchResults.style.maxHeight = 'none';
      this.predictiveSearchResults.style.overflowY = 'auto';
    } else {
      const inDrawer = !!this.closest('.menu-drawer');
      const narrow = window.matchMedia('(max-width: 989px)').matches;

      if (inDrawer && narrow && this.input) {
        refineDrawerPredictiveTop = true;
        // Pull panel out of form layout first so rects aren’t polluted by in-flow sibling height.
        this.predictiveSearchResults.style.position = 'fixed';
        this.predictiveSearchResults.style.left = '0';
        this.predictiveSearchResults.style.right = '0';
        this.predictiveSearchResults.style.width = '100%';
        this.predictiveSearchResults.style.maxWidth = '100%';
        const topPx = this.drawerPredictiveTopPx();
        this.predictiveSearchResults.style.top = `${topPx}px`;
        this.predictiveSearchResults.style.bottom = `${bottomPx}px`;
        this.predictiveSearchResults.style.height = 'auto';
        this.predictiveSearchResults.style.maxHeight = 'none';
        this.predictiveSearchResults.style.overflowY = 'auto';
        this.predictiveSearchResults.style.zIndex = '1250';
        this.predictiveSearchResults.style.backgroundColor = '#fafafa';
      } else {
        const maxPx = this.resultsMaxHeight || this.getResultsMaxHeight();
        this.predictiveSearchResults.style.maxHeight = `${maxPx}px`;
        this.predictiveSearchResults.style.overflowY = 'auto';
        this.predictiveSearchResults.style.minHeight = '0';
      }
    }
    
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;

    // Drawer: second layout pass so `top` tracks the real field row after `[open]` paints.
    if (refineDrawerPredictiveTop && this.predictiveSearchResults) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (!this.isOpen || !this.predictiveSearchResults) return;
          this.predictiveSearchResults.style.top = `${this.drawerPredictiveTopPx()}px`;
        });
      });
    }
  }

  close(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }

    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    
    // Clear all inline styles
    this.predictiveSearchResults.removeAttribute('style');

    this.isOpen = false;
  }
}

customElements.define('predictive-search', PredictiveSearch);

// Handle clear button functionality
function toggleClearButton(searchInput, clearButton) {
  if (searchInput && clearButton) {
    if (searchInput.value.trim().length > 0) {
      clearButton.classList.add('active');
    } else {
      clearButton.classList.remove('active');
    }
  }
}

document.addEventListener('DOMContentLoaded', function() {
  document.querySelectorAll('.search-modal, details-modal').forEach(modal => {
    const searchInput = modal.querySelector('input[type="search"]');
    const clearButton = modal.querySelector('.search-modal__clear-btn');
    
    if (!searchInput || !clearButton) return;
    
    toggleClearButton(searchInput, clearButton);
    
    searchInput.addEventListener('input', function() {
      toggleClearButton(searchInput, clearButton);
    });
    
    searchInput.addEventListener('focus', function() {
      toggleClearButton(searchInput, clearButton);
    });
    
    clearButton.addEventListener('click', function(e) {
      e.preventDefault();
      e.stopPropagation();
      
      searchInput.value = '';
      searchInput.focus();
      
      toggleClearButton(searchInput, clearButton);

      const predictiveSearch = modal.querySelector('predictive-search');
      if (predictiveSearch && typeof predictiveSearch.close === 'function') {
        predictiveSearch.close(true);
      }

      searchInput.dispatchEvent(new Event('input', { bubbles: true }));
    });
  });
});
